import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TitleHeaderComponent } from 'app/shared/components/title-header/title-header.component';
import { TabsWithContentComponent } from 'app/shared/components/tabs-with-content/tabs-with-content.component';
import { CaseFormSectionContainerComponent } from 'app/shared/components/forms/case-form-section-container/case-form-section-container.component';
import { CommonModule } from '@angular/common';
import { NewsProfileComponent } from "./news/news-profile.component";
import { ConfigureInformationComponent } from "./configure-info/configure-information.component";
import { ImpersonateUserComponent } from "./impersonate-user/impersonate-user.component";
import { PersonalInformationComponent } from "../shared/components/forms/personal-information-form/personal-information.component";
import { NotificationsFormComponent } from "../shared/components/forms/notifications-form/notifications-form.component";
import { ProfileInformationComponent } from "../shared/components/profile-information/profile-information.component";
import { UpdateUserRequest, User } from './profile.model';
import { UserService } from './profile.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Tab } from 'app/shared/enums/profile.enum';
import { AuthService } from 'app/shared/services/auth.service';
import { USER_ROLES } from 'app/shared/enums/user-roles.enum';
import { ICredentials } from 'app/shared/models/credentials.model';
import { DiscardChangesService } from 'app/shared/services/discard-changes.service';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';
import { Subject, takeUntil } from 'rxjs';


@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    RouterLink,
    CommonModule,
    ReactiveFormsModule,
    TabsWithContentComponent,
    TitleHeaderComponent,
    NewsProfileComponent,
    ConfigureInformationComponent,
    ImpersonateUserComponent,
    PersonalInformationComponent,
    CaseFormSectionContainerComponent,
    NotificationsFormComponent,
    ProfileInformationComponent,
    TranslateModule
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent implements OnInit, OnDestroy {
  public Tab = Tab;
  private destroy$ = new Subject<void>();
  successMessage: string = '';
  tabs: string[] = [Tab.Profile];
  selectedTab: Tab = Tab.Profile;
  user: User | null = null;
  personalInfoForm!: FormGroup;
  hideTabs: boolean = false;
  credentials!: ICredentials | undefined;
  @ViewChild(NotificationsFormComponent) notificationsFormComponent!: NotificationsFormComponent;
  @ViewChild(PersonalInformationComponent) personalInformationComponent!: PersonalInformationComponent;

  constructor(
    private router: Router,
    private userService: UserService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private authService: AuthService,
    private discardChangesService: DiscardChangesService
  ) { }

  ngOnInit(): void {
    this.credentials = this.authService.getCredentials();
    this.setSelectedTabBasedOnUrl();
    this.loadUserData();
    if(this.credentials && this.credentials.role === USER_ROLES.ADMIN_USER) {
        this.tabs = [Tab.Profile, Tab.News, Tab.ConfigureInformation, Tab.ImpersonateUser];
    }

    this.router.events
    .pipe(takeUntil(this.destroy$))
    .subscribe(event => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects || event.url;
        if (url === '/admin') {
          this.setSelectedTabBasedOnUrl();
          this.loadUserData(); 
        }
      }
    });
  }

  setSelectedTabBasedOnUrl(): void {
    const currentUrl = this.router.url;

    if (currentUrl.includes('/admin/news')) {
      this.selectedTab = Tab.News;
    } else if (currentUrl.includes('/admin/configure-information')) {
      this.selectedTab = Tab.ConfigureInformation;
    } else if (currentUrl.includes('/admin/impersonate-user')) {
      this.selectedTab = Tab.ImpersonateUser;
    } else if (currentUrl === '/admin') {
      this.selectedTab = Tab.Profile;
    } else {
      this.selectedTab = Tab.Profile;
    }
  }

  navigateToTab(tab: string): void {
    this.discardChangesService.canDeactivate().then(
      (result) => {
        if(result) {

          this.selectedTab = tab as Tab;

          switch (this.selectedTab) {
            case Tab.News:
              this.router.navigate(['/admin/news']);
              break;
            case Tab.ConfigureInformation:
              this.router.navigate(['/admin/configure-information']);
              break;
            case Tab.ImpersonateUser:
              this.router.navigate(['/admin/impersonate-user']);
              break;
            default:
              this.router.navigate(['/admin']);
              break;
          }
        }
      }
    );
  }

  loadUserData(): void {
    this.userService.getUser().subscribe(
      (data: User) => this.user = data
    );
  }

  toggleTabsVisibility(shouldHide: boolean): void {
    this.hideTabs = shouldHide;
  }

  get isFormDirty(): boolean {
    return (
      this.notificationsFormComponent?.notificationsForm.dirty ||
      this.personalInformationComponent?.isGlobalCustomerNameDirty
    );
  }

  discardChanges(): void {
    if (this.notificationsFormComponent) {
      this.notificationsFormComponent.resetForm();
    }
    if (this.personalInformationComponent) {
      this.personalInformationComponent.resetForm();
    }
  }

  saveChanges(): void {
    if (!this.user) return;

    const updateData: UpdateUserRequest = {
      globalCustomerName: this.personalInformationComponent.globalCustomerName,
      notificationSettings: this.notificationsFormComponent.getNotificationSettings()
    };

    this.userService.updateUserSettings(updateData).subscribe({
      next: () => {
        const successMessage = this.translate.instant('messages.profile_update');
        this.toastr.show(successMessage, ToastTypesEnum.SUCCESS);
        this.notificationsFormComponent?.notificationsForm.markAsPristine();
        this.personalInformationComponent?.personalInfoForm.get('globalCustomerName')?.markAsPristine();
        this.loadUserData();
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
